import React, { createContext, useContext, useCallback, useState, useEffect, useMemo } from "react";
import { useCountry } from "./country";

const LangCtx = createContext(null);
const STORAGE_KEY = "tb_lang";

/**
 * Language behaviour by market:
 *  - SV (El Salvador): forced to "es". No toggle rendered.
 *  - US (United States): user picks between "en" (default) and "es". Persisted per-user in localStorage.
 */
export function LanguageProvider({ children }) {
    const { country } = useCountry();
    const [lang, setLangState] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored === "en" || stored === "es") return stored;
        } catch { /* ignore */ }
        return null;  // resolved by the effect below once we know the country
    });

    // Resolve default when the country changes.
    // For USA users with no stored preference, honor the browser locale: navigator.language
    // starting with "en" → English; anything else → Spanish. This lets US visitors coming from
    // English browsers land straight in English without hunting for the toggle.
    useEffect(() => {
        if (lang) return;
        if (country === "US") {
            let initial = "en";
            try {
                const browser = (navigator.language || navigator.userLanguage || "").toLowerCase();
                initial = browser.startsWith("es") ? "es" : "en";
            } catch { /* ignore */ }
            setLangState(initial);
        } else {
            setLangState("es");
        }
    }, [country, lang]);

    // SV forces Spanish regardless of the stored preference
    const effectiveLang = country === "SV" ? "es" : (lang || "en");

    const setLang = useCallback((next) => {
        if (next !== "es" && next !== "en") return;
        setLangState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    }, []);

    const t = useCallback((es, en) => (effectiveLang === "en" ? en : es), [effectiveLang]);

    const value = useMemo(() => ({
        lang: effectiveLang,
        setLang,
        t,
        isEnglish: effectiveLang === "en",
        isSpanish: effectiveLang === "es",
        canToggle: country === "US",
    }), [effectiveLang, setLang, t, country]);

    return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useLanguage() {
    const ctx = useContext(LangCtx);
    if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
    return ctx;
}
